import React, { useState, useEffect } from "react";
import { Avatar, Box, Stack, Typography, IconButton, CircularProgress } from "@mui/material";
import { useHistory } from "react-router-dom";
import ApiService from "../../services/api-service";
import { Images } from "../../assets/common";
import tipcolor from "../../assets/common/resources/tipcolor.svg";
import { useDispatch } from "react-redux";
import { addSongAction } from "../../action/songs";
import TipPopup from "../TipPopup";

const FeaturedSection = () => {
  const [displayData, setDisplayData] = useState([]);
  const [visibleItems, setVisibleItems] = useState(5);
  const [loading, setLoading] = useState(false);
  const [openTipPopup, setOpenTipPopup] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();

  // Fetch the featured music data
  const fetchMusicData = async () => {
    setLoading(true);
    try {
      const response = await ApiService.MUSIC_MOST_STREAM_WEEK();
      const enrichedData = await Promise.all(
        response.result.map(async (musicItem) => {
          const tipperResponse = await ApiService.INDIVIDUAL_SONG_TIPPER(musicItem._id);
          const topTipper = tipperResponse.result?.find((tipper) => tipper.musicId === musicItem._id) || null;
          return {
            ...musicItem,
            topTipperName: topTipper?.topTipperName || null,
            topTipperProfile: topTipper?.topTipperProfile?.filePath || Images.dummy,
          };
        })
      );
      setDisplayData(enrichedData);
    } catch (error) {
      console.error("Error fetching music data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMusicData();
  }, []);

  const handlePlaySong = (song) => {
    dispatch(addSongAction(song));
  };

  const handleTipClick = (song) => {
    setSelectedSong(song);
    setOpenTipPopup(true);
  };

  const handleTipPopupClose = () => {
    setOpenTipPopup(false);
    setSelectedSong(null);
  };

  const handleArtistClick = (artistId) => {
    history.push(`/app/ArtistProfile/${artistId}`);
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  return (
    <Box sx={{ padding: "20px", borderRadius: "12px", backgroundColor: "#101010" }}>
      {/* Section Header */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: "600",
          color: "#FFFFFF",
          marginBottom: "20px",
          textAlign: "left",
          fontFamily: "Inter, sans-serif",
          letterSpacing: "0.3px",
        }}
      >
        Most Streamed This Week
      </Typography>

      {/* Column Titles */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr", // Adjust column proportions
          padding: "10px 15px",
          backgroundColor: "#181818",
          borderRadius: "8px",
          marginBottom: "10px",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: "500", fontSize: "12px", color: "#9E9E9E", textTransform: "uppercase" }}>
          Title
        </Typography>
        <Typography sx={{ fontWeight: "500", fontSize: "12px", color: "#9E9E9E", textTransform: "uppercase" }}>
          Artist
        </Typography>
        <Typography sx={{ fontWeight: "500", fontSize: "12px", color: "#9E9E9E", textAlign: "center", textTransform: "uppercase" }}>
          Streams
        </Typography>
        <Typography sx={{ fontWeight: "500", fontSize: "12px", color: "#9E9E9E", textAlign: "center", textTransform: "uppercase" }}>
          Duration
        </Typography>
        <Typography sx={{ fontWeight: "500", fontSize: "12px", color: "#9E9E9E", textAlign: "center", textTransform: "uppercase" }}>
          Top Tipper
        </Typography>
      </Box>

      {/* Content Rows */}
      <Box sx={{ maxHeight: "400px", overflowY: "auto", scrollbarWidth: "thin", pr: 1 }}>
        {loading ? (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <CircularProgress size={30} color="inherit" />
          </Box>
        ) : displayData.length > 0 ? (
          displayData.slice(0, visibleItems).map((musicItem) => (
            <Box
              key={musicItem?._id}
              sx={{
                display: "grid",
                gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr", // Match column layout to header
                padding: "10px 15px",
                backgroundColor: "#1C1C1C",
                borderRadius: "8px",
                alignItems: "center",
                marginBottom: "8px",
                transition: "background-color 0.3s ease",
                "&:hover": { backgroundColor: "#2A2A2A" },
              }}
            >
              {/* Title (Thumbnail and Text) */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ cursor: "pointer" }}
                onClick={() => handlePlaySong(musicItem)}
              >
                <Avatar
                  src={musicItem?.thumbnailImg?.filePath || Images.dummy}
                  alt={musicItem.title}
                  sx={{ width: 40, height: 40, borderRadius: "8px" }}
                />
                <Typography sx={{ color: "#FFFFFF", fontWeight: "600", fontSize: "14px" }}>
                  {musicItem.title}
                </Typography>
              </Stack>

              {/* Artist */}
              <Typography
                sx={{
                  color: "#FFEE20",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
                onClick={() => handleArtistClick(musicItem.uploader)}
              >
                {musicItem.artistData?.[0]?.fullName || "Unknown Artist"}
              </Typography>

              {/* Streams */}
              <Typography sx={{ color: "#FFFFFF", textAlign: "center", fontSize: "14px" }}>
                {Math.round(musicItem.streamCount)}
              </Typography>

              {/* Duration */}
              <Typography sx={{ color: "#FFFFFF", textAlign: "center", fontSize: "14px" }}>
                {formatDuration(musicItem.duration)}
              </Typography>

              {/* Top Tipper */}
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                {musicItem.topTipperName ? (
                  <>
                    <Avatar
                      src={musicItem?.topTipperProfile || Images.dummy}
                      alt={musicItem.topTipperName}
                      sx={{ width: 30, height: 30 }}
                    />
                    <Typography sx={{ color: "#FFFFFF", fontSize: "14px" }}>
                      {musicItem.topTipperName}
                    </Typography>
                  </>
                ) : (
                  <IconButton onClick={() => handleTipClick(musicItem)}>
                    <img src={tipcolor} alt="tip" style={{ width: "20px", height: "20px" }} />
                  </IconButton>
                )}
              </Stack>
            </Box>
          ))
        ) : (
          <Typography sx={{ textAlign: "center", color: "#FFFFFF" }}>No data available</Typography>
        )}
      </Box>

      {/* Tip Popup */}
      {selectedSong && (
        <TipPopup
          artist={selectedSong.artistData?.[0]?.fullName || "Unknown Artist"}
          id={selectedSong._id}
          open={openTipPopup}
          isArtist={false}
          handleClose={handleTipPopupClose}
        />
      )}
    </Box>
  );
};

export default FeaturedSection;
